import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseApiPath } from '@/app/common/api/config';
import customFetchBase from '@/app/common/utils/customFetchBase';
import type {
  GetOrderByIdRequest,
  GetOrderByIdResponse,
  GetOrdersData,
  InvoiceIssuanceRequest,
  LastOrderData,
  OrderRegisterRequest,
  OrdersApiResponse,
  RePaymentMode,
  ShowFactorRequest,
  ShowInvoiceRequest,
  TariffSlugRequest,
  ValidateIntroCodeRequest,
  ValidateIntroCodeResponse,
} from '@/app/Purchase/types/ordersApiTypes';
import type { LegalFormValues } from '@/app/Purchase/types/purchaseTypes';

const ordersApi = createApi({
  reducerPath: 'orders',
  baseQuery: (...args) => customFetchBase(getBaseApiPath(), ...args),
  tagTypes: ['Orders'],
  endpoints: (build) => ({
    getLastOrder: build.query<OrdersApiResponse<LastOrderData>, void>({
      query: () => ({
        url: '/go_handler/v2/order/get_last_order',
        method: 'POST',
      }),
    }),
    getSelectedTariffBySlug: build.query<
      OrdersApiResponse<Record<string, unknown>>,
      TariffSlugRequest
    >({
      query: (body) => ({
        url: '/go_handler/v2/order/get_selected_tariff_by_slug',
        method: 'POST',
        body,
      }),
    }),
    getOrders: build.query<OrdersApiResponse<GetOrdersData>, void>({
      query: () => ({
        url: '/go_handler/v2/order/get2',
        method: 'POST',
      }),
      providesTags: ['Orders'],
    }),
    getOrderById: build.query<GetOrderByIdResponse, GetOrderByIdRequest>({
      query: (body) => ({
        url: '/go_handler/v2/order/get_order_by_id',
        method: 'POST',
        body,
      }),
    }),
    validateIntroCode: build.mutation<
      ValidateIntroCodeResponse,
      ValidateIntroCodeRequest
    >({
      query: (body) => ({
        url: '/go_handler/v2/intro_code/validate',
        method: 'POST',
        body,
      }),
    }),
    showInvoice: build.mutation<
      OrdersApiResponse<Record<string, unknown>>,
      ShowInvoiceRequest
    >({
      query: (body) => ({
        url: '/go_handler/v2/order/show_invoice',
        method: 'POST',
        body,
      }),
    }),
    getInvoice: build.query<
      OrdersApiResponse<Record<string, unknown>>,
      ShowInvoiceRequest
    >({
      query: (body) => ({
        url: '/go_handler/v2/order/show_invoice',
        method: 'POST',
        body,
      }),
    }),
    insertRealOrLegal: build.mutation<
      OrdersApiResponse<Record<string, unknown>>,
      Record<string, unknown>
    >({
      query: (body) => ({
        url: '/go_handler/v2/order/insert_real_and_legal',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Orders'],
    }),
    showRealOrLegal: build.mutation<
      OrdersApiResponse<LegalFormValues>,
      void
    >({
      query: () => ({
        url: '/go_handler/v2/order/show_real_and_legal',
        method: 'POST',
      }),
    }),
    orderRegister: build.mutation<
      OrdersApiResponse<Record<string, unknown>>,
      OrderRegisterRequest
    >({
      query: (body) => ({
        url: '/go_handler/v2/order/registration',
        method: 'POST',
        body,
      }),
    }),
    showFactor: build.query<
      OrdersApiResponse<Record<string, unknown>>,
      ShowFactorRequest
    >({
      query: (body) => ({
        url: '/go_handler/v2/order/show_factor',
        method: 'POST',
        body,
      }),
    }),
    invoiceIssuance: build.mutation<
      OrdersApiResponse<Record<string, unknown>>,
      InvoiceIssuanceRequest
    >({
      query: (body) => ({
        url: '/go_handler/v2/order/invoice_issuance',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Orders'],
    }),
    rePayment: build.mutation<
      OrdersApiResponse<Record<string, unknown>>,
      RePaymentMode | void
    >({
      query: (mode) => ({
        url: `/go_handler/v2/order/${mode === 'membership' ? 'payment' : 're_payment'}`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetLastOrderQuery,
  useGetSelectedTariffBySlugQuery,
  useValidateIntroCodeMutation,
  useShowInvoiceMutation,
  useInsertRealOrLegalMutation,
  useOrderRegisterMutation,
  useRePaymentMutation,
  useGetInvoiceQuery,
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useLazyGetOrderByIdQuery,
  useShowRealOrLegalMutation,
  useShowFactorQuery,
  useInvoiceIssuanceMutation,
} = ordersApi;

export default ordersApi;
